import { IBoardSlot, IBoardSnapshot } from '../../../dtos/IBoardSnapshot';

export const NEXT_SLOTS_LIMIT = 8;

export interface IScheduleView {
  /** Rodadas em andamento, uma por arena. */
  ongoing: IBoardSlot[];
  /** Intervalo acontecendo agora, se houver. */
  currentBreak: IBoardSlot | null;
  /** Próximas rodadas (e intervalos futuros) em ordem de horário. */
  next: IBoardSlot[];
}

const time = (iso: string) => new Date(iso).getTime();

/**
 * O servidor manda uma janela fixa do cronograma; quem decide o que já passou
 * é o relógio da tela. Rodada atrasada (horário vencido mas ainda não aberta)
 * continua em "próximas": só some quando o juiz abre ou fecha.
 */
export function splitSchedule(
  upcoming: IBoardSlot[],
  now: number,
  limit = NEXT_SLOTS_LIMIT,
): IScheduleView {
  const sorted = [...upcoming].sort(
    (a, b) => time(a.starts_at) - time(b.starts_at),
  );

  const ongoing = sorted.filter(s => !s.is_break && s.status === 'ongoing');

  const currentBreak =
    sorted.find(
      s => s.is_break && time(s.starts_at) <= now && time(s.ends_at) > now,
    ) ?? null;

  const next = sorted
    .filter(s =>
      s.is_break ? time(s.starts_at) > now : s.status === 'scheduled',
    )
    .slice(0, limit);

  return { ongoing, currentBreak, next };
}

export type TBoardPhase = 'before' | 'live' | 'after';

export interface IBoardPhase {
  phase: TBoardPhase;
  /** Início da primeira rodada, para a contagem regressiva. */
  firstStart: string | null;
}

export function boardPhase(snapshot: IBoardSnapshot): IBoardPhase {
  const { tournament, last_rounds, upcoming } = snapshot;
  const teamSlots = upcoming.filter(s => !s.is_break);
  const firstStart =
    teamSlots.map(s => s.starts_at).sort((a, b) => time(a) - time(b))[0] ??
    null;

  if (tournament.status === 'completed' || tournament.status === 'cancelled') {
    return { phase: 'after', firstStart };
  }

  const anyOngoing = teamSlots.some(s => s.status === 'ongoing');

  if (last_rounds.length === 0 && !anyOngoing) {
    return { phase: 'before', firstStart };
  }

  if (last_rounds.length > 0 && teamSlots.length === 0) {
    return { phase: 'after', firstStart: null };
  }

  return { phase: 'live', firstStart };
}

/** "02:15:09" / "15:09" até o horário alvo; null se já passou. */
export function countdown(
  targetIso: string | null,
  now: number,
): string | null {
  if (!targetIso) return null;
  const diff = Math.floor((time(targetIso) - now) / 1000);
  if (diff <= 0) return null;

  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  const pad = (n: number) => String(n).padStart(2, '0');

  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}
